import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchDoll, resolveImage } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { ArrowLeft } from "lucide-react";

export default function DollStory() {
  const { id } = useParams();
  const [doll, setDoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchDoll(id)
      .then((d) => setDoll(d))
      .catch((e) => setError(e?.response?.status === 404 ? "She is not in the cabinet." : "Something went wrong."))
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <Link
          to="/"
          data-testid="back-to-exhibit"
          className="inline-flex items-center gap-2 font-ui text-xs tracking-eerie uppercase text-[#a89f91] hover:text-[#e8aa42] transition-colors duration-500"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.25} />
          Back to the Girls
        </Link>
      </div>

      {loading && (
        <div
          data-testid="story-loading"
          className="max-w-7xl mx-auto px-6 md:px-12 py-24 font-ui text-xs tracking-eerie uppercase text-[#a89f91]"
        >
          Lifting the glass…
        </div>
      )}

      {error && (
        <div
          data-testid="story-error"
          className="max-w-7xl mx-auto px-6 md:px-12 py-24 font-display text-3xl text-[#e2d6c8]"
        >
          {error}
        </div>
      )}

      {doll && (
        <article className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-12 gap-10 md:gap-16">
          {/* Portrait */}
          <div className="col-span-12 md:col-span-5 lg:col-span-5">
            <div className="md:sticky md:top-28">
              <div
                data-testid="story-image"
                className="relative overflow-hidden doll-arch border border-[#e8aa42]/30 bg-[#1a1614] shadow-[0_16px_64px_rgba(0,0,0,0.6)] aspect-[2/3]"
              >
                <img
                  src={resolveImage(doll.image_url)}
                  alt={doll.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0c0b]/40 via-transparent to-transparent pointer-events-none" />
              </div>
              <p className="font-ui text-[10px] tracking-eerie uppercase text-[#e8aa42]/80 mt-5">
                N° {String(doll.order).padStart(2, "0")} · One of Vicky's Lost Girls
              </p>
            </div>
          </div>

          {/* Story */}
          <div className="col-span-12 md:col-span-7 lg:col-span-7">
            <p
              data-testid="story-eyebrow"
              className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-4"
            >
              Her Story
            </p>
            <h1
              data-testid="story-title"
              className="font-display font-light text-6xl md:text-7xl lg:text-8xl leading-none text-[#e2d6c8] mb-10"
            >
              {doll.name}
            </h1>
            <div className="h-px w-24 bg-[#8b0000] mb-10" />
            <div
              data-testid="story-body"
              className="font-body text-lg md:text-xl leading-relaxed text-[#cdbfb0] drop-cap whitespace-pre-wrap"
            >
              {doll.story}
            </div>
          </div>
        </article>
      )}
    </div>
  );
}
